import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import articles from '../../articles/articleIndex'
// import logoSvg from '../../images/samLogo.png'
import './articles.scss'

const articleHashPrefix = '#article/'
const defaultSiteTitle = 'Articles'
const structuredDataId = 'article-structured-data'

const MermaidDiagram = ({ chart }) => {
  const containerRef = useRef(null)
  const renderId = `article-mermaid-${useId().replace(/:/g, '')}`
  const [renderError, setRenderError] = useState('')

  useEffect(() => {
    let isCurrent = true

    const renderDiagram = async () => {
      if (!containerRef.current) {
        return
      }

      setRenderError('')
      containerRef.current.innerHTML = ''

      try {
        const mermaidModule = await import('mermaid')
        const mermaid = mermaidModule.default || mermaidModule

        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: 'dark',
          themeVariables: {
            primaryColor: '#0c1624',
            primaryTextColor: '#dce9f4',
            primaryBorderColor: '#7ce8ff',
            lineColor: '#7e8d9d',
            secondaryColor: '#101928',
            tertiaryColor: '#0b101b',
            edgeLabelBackground: '#030712'
          }
        })

        const renderResult = await mermaid.render(renderId, chart.trim())
        const svg = typeof renderResult === 'string' ? renderResult : renderResult.svg

        if (!isCurrent || !containerRef.current || !svg) {
          return
        }

        containerRef.current.innerHTML = svg

        if (typeof renderResult !== 'string') {
          renderResult.bindFunctions?.(containerRef.current)
        }
      } catch {
        if (isCurrent) {
          setRenderError('Unable to render this Mermaid diagram.')
        }
      }
    }

    renderDiagram()

    return () => {
      isCurrent = false
    }
  }, [chart, renderId])

  return (
    <div className='article_mermaid' ref={containerRef} role='img' aria-label='Mermaid diagram'>
      {renderError && <div className='article_mermaid_error'>{renderError}</div>}
    </div>
  )
}

const getArticleSlugFromHash = () => {
  if (!window.location.hash.startsWith(articleHashPrefix)) {
    return ''
  }

  return decodeURIComponent(window.location.hash.slice(articleHashPrefix.length))
}

const getArticleFromHash = () => {
  const slug = getArticleSlugFromHash()
  return articles.find(article => article.slug === slug) || null
}

const setMetaTag = (attribute, key, content) => {
  const selector = `meta[${attribute}="${key}"]`
  let element = document.head.querySelector(selector)

  if (!content) {
    element?.remove()
    return
  }

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.appendChild(element)
  }

  element.setAttribute('content', content)
}

const setRepeatedMetaTags = (attribute, key, values = []) => {
  document.head.querySelectorAll(`meta[${attribute}="${key}"]`).forEach(element => element.remove())

  values.filter(Boolean).forEach(value => {
    const element = document.createElement('meta')
    element.setAttribute(attribute, key)
    element.setAttribute('content', value)
    document.head.appendChild(element)
  })
}

const updateStructuredData = article => {
  const existingScript = document.getElementById(structuredDataId)

  if (!article) {
    existingScript?.remove()
    return
  }

  const imageUrl = article.imagePath
    ? new URL(article.imagePath, window.location.origin).href
    : undefined

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: article.author ? { '@type': 'Person', name: article.author } : undefined,
    keywords: article.tags,
    image: imageUrl ? [imageUrl] : undefined,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': window.location.href
    }
  }

  const script = existingScript || document.createElement('script')
  script.id = structuredDataId
  script.type = 'application/ld+json'
  script.textContent = JSON.stringify(structuredData)

  if (!existingScript) {
    document.head.appendChild(script)
  }
}

const updateArticleMetadata = article => {
  const pageTitle = article ? `${article.title} | Portfolio` : 'Articles | Portfolio'
  const description =
    article?.excerpt ||
    'Notes on systems, engineering, product thinking, and the work behind the projects.'
  const imageUrl = article?.imagePath
    ? new URL(article.imagePath, window.location.origin).href
    : ''

  document.title = pageTitle

  setMetaTag('name', 'description', description)
  setMetaTag('name', 'keywords', article?.tags?.join(', '))
  setMetaTag('name', 'author', article?.author)
  setRepeatedMetaTags('property', 'article:author', article?.author ? [article.author] : [])
  setMetaTag('property', 'og:title', pageTitle)
  setMetaTag('property', 'og:description', description)
  setMetaTag('property', 'og:type', article ? 'article' : 'website')
  setMetaTag('property', 'og:url', window.location.href)
  setMetaTag('property', 'og:image', imageUrl)
  setMetaTag('property', 'og:image:alt', article?.title)
  setMetaTag('property', 'og:site_name', 'Portfolio')
  setMetaTag('name', 'twitter:card', imageUrl ? 'summary_large_image' : 'summary')
  setMetaTag('name', 'twitter:title', pageTitle)
  setMetaTag('name', 'twitter:description', description)
  setMetaTag('name', 'twitter:image', imageUrl)
  setMetaTag('name', 'twitter:image:alt', article?.title)

  setRepeatedMetaTags(
    'property',
    'article:published_time',
    article ? [new Date(`${article.date}T12:00:00`).toISOString()] : []
  )
  setRepeatedMetaTags('property', 'article:tag', article?.tags || [])
  updateStructuredData(article)
}

const Articles = React.forwardRef((props, ref) => {
  const [selectedArticle, setSelectedArticle] = useState(() => getArticleFromHash())
  const [articleContent, setArticleContent] = useState('')
  const [articleContentError, setArticleContentError] = useState(false)

  const articleCount = useMemo(() => String(articles.length).padStart(2, '0'), [])

  useEffect(() => {
    const handleHashChange = () => {
      setSelectedArticle(getArticleFromHash())
    }

    window.addEventListener('hashchange', handleHashChange)

    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  useEffect(() => {
    updateArticleMetadata(selectedArticle)
  }, [selectedArticle])

  useEffect(() => {
    let isCurrent = true

    if (!selectedArticle) {
      setArticleContent('')
      setArticleContentError(false)
      return () => {
        isCurrent = false
      }
    }

    setArticleContent('')
    setArticleContentError(false)

    fetch(selectedArticle.contentUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load article (${response.status})`)
        }
        return response.text()
      })
      .then(markdown => {
        if (isCurrent) {
          setArticleContent(markdown)
        }
      })
      .catch(() => {
        if (isCurrent) {
          setArticleContentError(true)
        }
      })

    return () => {
      isCurrent = false
    }
  }, [selectedArticle])

  const formatDate = date =>
    new Intl.DateTimeFormat('en', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    }).format(new Date(`${date}T12:00:00`))

  const openArticle = article => {
    window.location.hash = `${articleHashPrefix}${encodeURIComponent(article.slug)}`
  }

  const closeArticle = () => {
    if (window.location.hash.startsWith(articleHashPrefix)) {
      window.history.pushState('', document.title, window.location.pathname + window.location.search)
    }

    setSelectedArticle(null)
  }

  const goHome = () => {
    closeArticle()
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    })
  }

  return (
    <div ref={ref} className='articles_section'>
      <div className='articles_intro'>
        <div>
          <div className='articles_overline'>KNOWLEDGE LOG / {articleCount}</div>
          <h1>Articles</h1>
        </div>
        <p>
          Notes on systems, engineering, product thinking, and the work behind the
          projects.
        </p>
      </div>

      <div className='articles_grid'>
        {articles.length === 0 ? (
          <div className='articles_empty'>
            <span>NO ARTICLES INDEXED</span>
            <p>Add a Markdown file under <code>src/articles</code> and register it in <code>articleIndex.js</code>.</p>
          </div>
        ) : (
          articles.map((article, index) => (
            <article className='article_card' key={article.slug}>
              <button
                type='button'
                className='article_card_button'
                onClick={() => openArticle(article)}
                aria-label={`Open article: ${article.title}`}
              >
                <div className='article_card_media'>
                  {article.imagePath ? (
                    <img src={article.imagePath} alt='' loading='lazy' />
                  ) : (
                    <div className='article_card_media_fallback' aria-hidden='true'>
                      <span>{String(index + 1).padStart(2, '0')}</span>
                    </div>
                  )}
                  <span className='article_card_media_label'>READ / {article.readTime}</span>
                </div>

                <div className='article_card_body'>
                  <div className='article_card_meta'>
                    <span>{formatDate(article.date)}</span>
                    <span>ENTRY {String(index + 1).padStart(2, '0')}</span>
                  </div>

                  <h2>{article.title}</h2>
                  <p>{article.excerpt}</p>

                  <div className='article_card_footer'>
                    <div className='article_tags'>
                      {article.tags.map(tag => (
                        <span key={tag}>{tag}</span>
                      ))}
                    </div>
                    <span className='article_open_mark'>↗</span>
                  </div>
                </div>
              </button>
            </article>
          ))
        )}
      </div>

      <Dialog
        fullScreen
        open={Boolean(selectedArticle)}
        onClose={closeArticle}
        className='article_reader_dialog'
      >
        {selectedArticle && (
          <div className='article_reader'>
            <header className='article_reader_header'>
              <div className='article_reader_nav'>
                <button
                  type='button'
                  className='article_reader_home'
                  onClick={goHome}
                  aria-label='Return to home'
                >
                  <svg viewBox='0 0 24 24' aria-hidden='true'>
                    <path d='M3.5 10.5 12 3l8.5 7.5' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
                    <path d='M5.5 9.5V20h13V9.5' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinejoin='round' />
                    <path d='M9.5 20v-5.5h5V20' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinejoin='round' />
                  </svg>
                  <span>HOME</span>
                </button>

                <div className='article_reader_summary'>
                  <span className='article_reader_kicker'>ARTICLE</span>
                  <span className='article_reader_separator' aria-hidden='true'>/</span>
                  <span>{formatDate(selectedArticle.date)}</span>
                  <span className='article_reader_separator' aria-hidden='true'>/</span>
                  <span>{selectedArticle.readTime}</span>
                </div>
              </div>

              <IconButton
                onClick={closeArticle}
                aria-label='Close article'
                className='article_reader_close'
              >
                <span aria-hidden='true'>×</span>
              </IconButton>
            </header>

            <div className='article_reader_body'>
              {selectedArticle.imagePath && (
                <img
                  className='article_reader_hero'
                  src={selectedArticle.imagePath}
                  alt=''
                />
              )}

              {articleContentError ? (
                <p className='article_reader_error'>Unable to load this article.</p>
              ) : articleContent ? (
                <ReactMarkdown
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    a: ({ node, ...props }) => (
                      <a {...props} target='_blank' rel='noreferrer' />
                    ),
                    code: ({ node, inline, className, children, ...props }) => {
                      const language = /language-(\w+)/.exec(className || '')?.[1]

                      if (!inline && language === 'mermaid') {
                        return <MermaidDiagram chart={String(children).replace(/\n$/, '')} />
                      }

                      return (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      )
                    },
                    img: ({ node, ...props }) => (
                      <img {...props} loading='lazy' decoding='async' />
                    ),
                    video: ({ node, ...props }) => (
                      <video {...props} className={`article_reader_video ${props.className || ''}`.trim()} controls preload='metadata' />
                    ),
                    source: ({ node, ...props }) => <source {...props} />,
                    iframe: ({ node, ...props }) => (
                      <iframe {...props} className={`article_reader_embed ${props.className || ''}`.trim()} loading='lazy' title={props.title || 'Embedded media'} />
                    )
                  }}
                >
                  {articleContent}
                </ReactMarkdown>
              ) : (
                <p className='article_reader_loading'>Loading article...</p>
              )}
            </div>

            <button
              type='button'
              className='article_reader_top'
              onClick={() => {
                document.querySelector('.article_reader')?.scrollTo({ top: 0, behavior: 'smooth' })
              }}
              aria-label='Move to top of article'
            >
              <span aria-hidden='true'>↑</span>
              MOVE TO TOP
            </button>
          </div>
        )}
      </Dialog>
    </div>
  )
})

export default Articles
